import React, { useId, useMemo, useRef, useState } from 'react';
import { MarkButton } from '../../../components/MarkControls';
import useStepGraph from '../../../hooks/useStepGraph';
import type { DripStep } from '../../../types';
import { layoutJourney } from './journeyLayout';
import type { JourneyConnection } from './journeyLayout';
import ExitCard from './ExitCard';
import * as styles from './journey.module.css';

type Props = {
    steps: DripStep[];
    headerActions?: React.ReactNode;
    entryStepId: string | null;
    renderStep: (
        step: DripStep,
        context: { isEntry: boolean; hasLegalCycle: boolean }
    ) => React.ReactNode;
    renderBranch?: (connection: JourneyConnection) => React.ReactNode;
};
const JourneyCanvas: React.FC<Props> = ({
    steps,
    entryStepId,
    renderStep,
    renderBranch,
    headerActions
}) => {
    const graph = useStepGraph(steps, entryStepId);
    const journey = useMemo(() => layoutJourney(graph), [graph]);
    const [zoom, setZoom] = useState(1);
    const scroller = useRef<HTMLDivElement>(null);
    const marker = useId().replace(/:/g, '');
    const unreachable = entryStepId == null ? steps : graph.unreachable;
    const cycleSteps = new Set(
        graph.edges.filter((edge) => edge.isLegalCycle).flatMap((edge) => edge.cyclePath ?? [])
    );
    const entry = journey.nodes.find((node) => node.step?.id === entryStepId);
    const fit = () =>
        setZoom(
            Math.min(
                1,
                Math.max(
                    0.25,
                    ((scroller.current?.clientWidth ?? journey.width) - 16) / journey.width
                )
            )
        );
    return (
        <div className={styles.journey}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarCopy}>
                    <strong>
                        Journey · {steps.length} {steps.length === 1 ? 'step' : 'steps'}
                    </strong>
                    <p className={styles.hint}>
                        {renderBranch
                            ? 'Select a step to edit. Use + on a path to add a step.'
                            : 'Follow the paths from start to finish.'}
                    </p>
                </div>
                <div className={styles.zoom} aria-label="Journey zoom">
                    <MarkButton
                        compact
                        aria-label="Zoom out"
                        disabled={zoom <= 0.25}
                        onClick={() => setZoom((value) => Math.max(0.25, value - 0.1))}
                    >
                        −
                    </MarkButton>
                    <output className={styles.zoomOutput} aria-label="Zoom level">
                        {Math.round(zoom * 100)}%
                    </output>
                    <MarkButton
                        compact
                        aria-label="Zoom in"
                        disabled={zoom >= 1.5}
                        onClick={() => setZoom((value) => Math.min(1.5, value + 0.1))}
                    >
                        +
                    </MarkButton>
                    <MarkButton compact onClick={fit}>
                        Fit
                    </MarkButton>
                    {headerActions}
                </div>
            </div>
            <ul className={styles.srOnly} aria-label="Journey paths">
                {journey.connections.map((edge) => (
                    <li key={edge.key}>
                        {edge.source.id}: {edge.label} → {edge.isExit ? 'Finish' : edge.target}
                        {edge.isLegalCycle
                            ? ' (repeats)'
                            : edge.isIllegalCycle
                              ? ' (invalid loop)'
                              : ''}
                    </li>
                ))}
            </ul>
            {journey.nodes.length > 0 && (
                <div
                    ref={scroller}
                    className={styles.scroller}
                    tabIndex={0}
                    role="region"
                    aria-label="Drip journey"
                >
                    <div
                        className={styles.bounds}
                        style={{ width: journey.width * zoom, height: journey.height * zoom }}
                    >
                        <div
                            className={styles.scene}
                            style={{
                                width: journey.width,
                                height: journey.height,
                                transform: `scale(${zoom})`
                            }}
                        >
                            <svg
                                className={styles.connections}
                                width={journey.width}
                                height={journey.height}
                                aria-hidden="true"
                            >
                                <defs>
                                    <marker
                                        id={marker}
                                        viewBox="0 0 10 10"
                                        refX="9"
                                        refY="5"
                                        markerWidth="6"
                                        markerHeight="6"
                                        orient="auto-start-reverse"
                                    >
                                        <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--mark-muted)" />
                                    </marker>
                                </defs>
                                {entry && (
                                    <path
                                        className={styles.link}
                                        d={`M ${entry.x + entry.width / 2} 44 V ${entry.y}`}
                                        markerEnd={`url(#${marker})`}
                                    />
                                )}
                                {journey.connections.map((edge) => (
                                    <path
                                        key={edge.key}
                                        className={[
                                            styles.link,
                                            edge.isLegalCycle ? styles.loop : '',
                                            edge.isIllegalCycle ? styles.invalidLink : ''
                                        ].join(' ')}
                                        d={edge.path}
                                        markerEnd={`url(#${marker})`}
                                    />
                                ))}
                            </svg>
                            {entry && (
                                <div
                                    className={styles.start}
                                    style={{ left: entry.x + entry.width / 2 - 54 }}
                                >
                                    Start
                                </div>
                            )}
                            {journey.nodes.map((node) => (
                                <div
                                    key={node.key}
                                    className={styles.node}
                                    style={{
                                        left: node.x,
                                        top: node.y,
                                        width: node.width,
                                        height: node.height
                                    }}
                                >
                                    {node.kind === 'step' && node.step ? (
                                        <>
                                            {renderStep(node.step, {
                                                isEntry: node.step.id === entryStepId,
                                                hasLegalCycle:
                                                    cycleSteps.has(node.step.id) &&
                                                    node.step.type === 'check_segment' &&
                                                    node.step.wait_for != null
                                            })}
                                            {journey.connections
                                                .filter((edge) => edge.from === node.step?.id)
                                                .map((edge) => (
                                                    <div
                                                        key={edge.key}
                                                        className={styles.branch}
                                                        style={{
                                                            left: edge.labelX - node.x - 52,
                                                            top: edge.labelY - node.y - 18
                                                        }}
                                                    >
                                                        <span
                                                            className={[
                                                                styles.branchLabel,
                                                                edge.isLegalCycle
                                                                    ? styles.branchLoop
                                                                    : '',
                                                                edge.isIllegalCycle
                                                                    ? styles.branchInvalid
                                                                    : ''
                                                            ].join(' ')}
                                                            title={`${edge.label} → ${edge.isExit ? 'Finish' : edge.target}${edge.isLegalCycle ? ' · Repeats' : edge.isIllegalCycle ? ' · Invalid loop' : ''}`}
                                                        >
                                                            {edge.label}
                                                            {edge.isLegalCycle
                                                                ? ' ↻'
                                                                : edge.isIllegalCycle
                                                                  ? ' !'
                                                                  : ''}
                                                        </span>
                                                        {renderBranch?.(edge)}
                                                    </div>
                                                ))}
                                        </>
                                    ) : node.kind === 'finish' ? (
                                        <ExitCard reason={node.reason} />
                                    ) : (
                                        <div className={`${styles.finish} ${styles.missing}`}>
                                            <div>
                                                <strong>Missing step</strong>
                                                <p>{node.target}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {steps.length === 0 && (
                <div className={styles.empty}>
                    <h3>Your journey starts here</h3>
                    <p className={styles.hint}>Add a first step to build your drip.</p>
                </div>
            )}
            {unreachable.length > 0 && (
                <details className={styles.unreachable} open>
                    <summary>
                        {unreachable.length} unconnected{' '}
                        {unreachable.length === 1 ? 'step' : 'steps'}
                    </summary>
                    <p className={styles.hint}>
                        Not connected. Delete it, or add it again from an open end.
                    </p>
                    <div className={styles.unreachableGrid}>
                        {unreachable.map((step) => (
                            <div key={step.id}>
                                {renderStep(step, { isEntry: false, hasLegalCycle: false })}
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
};
export default JourneyCanvas;
